export const frontmatter = {
  title: "Scale.Farm Developer Update — February 27, 2026",
  date: "2026-02-27",
  description:
    "SuperVoter optimization engine, dashboard grand total, sortable vote table, Treasury page, and a wave of swap and UX improvements.",
  category: "news",
  authorName: "0xZod",
  authorInitials: "Zod",
  authorRole: "Lead Developer",
  readTimeMinutes: 3,
}

## Scale.Farm Developer Update — February 27, 2026

Beta merged to production. This update ships the most significant SuperVoter upgrade to date, a new Treasury page, dashboard improvements, and a round of UX polish across voting, swap, and navigation.

## SuperVoter Optimization Engine

The SuperVoter now has a full optimization engine with three strategy modes: Safe, Degen, and Beast. Each mode uses a different allocation profile. Beast uses the token-focused distribution algorithm that produces varied weightings — 51%, 36%, 12%, 1% — rather than the flat 20% per pool that the previous engine applied. The result is higher estimated returns for the same voting power.

<div style={{ paddingTop: "16px", paddingBottom: "40px" }}>
  <img
    src="/images/blog/supervoter-optimization-engine.png"
    alt="SuperVoter showing Beast allocation across pools — 51% WFRAX/SCALE, 36% SCALE/JANITOR, 12% NULL/SCALE, 1% SCALE/WOLF"
    style={{ width: "100%", borderRadius: "1rem" }}
  />
</div>

Alongside the strategy modes, a Token Strategy selector lets you focus incentives on a specific token from your locked NFTs. Select SCALE and the engine optimizes for maximum SCALE accumulation. Select USDC and it shifts the allocation accordingly. Estimates are computed in real time per token, cached for responsiveness, and displayed as dollar values next to each strategy so you can compare before committing. The selected strategy persists across wallet disconnect and NFT changes.

<div style={{ display: "flex", justifyContent: "center", padding: "1.5rem 0" }}>
  <img
    src="/images/blog/supervoter-strategy-dropdown.png"
    alt="Optimization Engine dropdown showing Safe ($13.57), Degen ($14.35), and Beast ($16.19) strategies alongside the Token Strategy selector"
    style={{ maxWidth: "320px", width: "100%", borderRadius: "1rem" }}
  />
</div>

## Dashboard Grand Total

The dashboard now shows a grand total row at the bottom of the summary section. It sums veSCALE, Classic Pools, CL Farms, and Earned Fees into a single Total Value figure alongside total Unclaimed Rewards. The `~` prefix marks values as approximate due to rounding across multiple async data sources. On mobile the two figures stack; on desktop they sit side by side. An APR column has also been added to the farming rewards tables.

## Sortable Vote Table

Every major column in the Revenue Sources list view is now sortable: Pair, Votes on Pool, Next Emissions, Voting APR, Fees and Bribes, My $ Per Vote, and Dilution Risk. Sort state persists to localStorage so your preferred ordering is remembered between sessions. The toggle between list and card view also received a fix — the thumb indicator now reflects the active state correctly.

## Treasury Page

A new Treasury page is available under the More menu. It links directly to the DeBank bundle so anyone can inspect the protocol treasury in one click.

## Swap Improvements

The swap engine now uses the WETH contract directly for wrap and unwrap operations instead of routing through swap aggregators. Native ETH swaps no longer trigger an unnecessary allowance or approval step. frxUSD has been added to the trending tokens list; USDbC has been retired from that list.

## UX and Navigation

The mobile menu has been updated: veSCALE now sits between Swap and Liquidity, which matches the logical flow of the product. Vote filter memory persists to localStorage with TVL and revenue defaulting to 0 on first load. Landing page polish landed across multiple sections — sentence case capitalization, consistent punctuation, Narratives heading alignment, and the "performing" label styled in blue in the top pools header. The landing page hero received mobile fixes to the gradient orbs and starfield animations.
